import { Button } from '@civilgrid/ui/components';
import { useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import { updateProfile } from '~/api/user2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import Form from '~/components2/forms/Form';
import Notify from '~/components2/Notify';
import rootStore from '../../../store';
import type { UserProfile } from './helpers';

type UserProfileContentProps = {
  placement: 'verification' | 'profile';
  onCompletion: () => void;
};

export default function UserProfileContent({ placement, onCompletion }: UserProfileContentProps) {
  const { me } = useSnapshot(rootStore) as typeof rootStore;

  const { handleSubmit, control, formState } = useForm<UserProfile>({
    defaultValues: {
      firstName: me?.firstName,
      lastName: me?.lastName,
      email: me?.email || '',
      title: me?.profile?.title || '',
      linkedInUrl: me?.profile?.linkedInUrl || '',
    },
  });

  const onSubmit: SubmitHandler<UserProfile> = async (data) => {
    const profileData: any = {
      ...data,
    };

    const response = await updateProfile(profileData);

    if (response.status === 200) {
      onCompletion();

      // Send only which fields changed, not their (PII) values. Email/name stay on the
      // person profile via setPosthogUser, not on the event.
      capturePosthogEvent(POSTHOG_EVENTS.userProfileUpdated, {
        placement,
        fields_updated: Object.keys(formState.dirtyFields),
      });

      // only show toast in profile
      if (placement === 'profile') {
        Notify.info({
          title: 'Success',
          message: 'Profile updated successfully',
        });
      }
      // refresh the me object in the root store
      rootStore.refreshMe();
    } else {
      onCompletion();
      Notify.error({
        title: 'Error',
        message: 'Failed to update profile',
      });
    }
  };

  useEffect(() => {
    capturePosthogEvent(POSTHOG_EVENTS.userProfileViewed, {
      placement,
    });
  }, [placement]);

  return (
    <div className="flex h-full flex-col justify-between space-y-4">
      <div>
        <Form>
          <div className="flex flex-col  space-y-2">
            <ControlledInput control={control} label="First Name" name="firstName" placeholder="Alice" />
            <ControlledInput control={control} label="Last Name" name="lastName" placeholder="Appleseed" />
            <ControlledInput control={control} label="Email" name="email" value={me?.email} disabled={true} />

            <ControlledInput
              control={control}
              label="Title"
              name="title"
              placeholder="Project Engineer, Survey Technician, Project Manager"
              tooltipText="Current job title"
            />
            <ControlledInput
              control={control}
              label="LinkedIn Profile URL"
              name="linkedInUrl"
              placeholder="https://www.linkedin.com/in/john-appleseed/"
              tooltipText={
                <div>
                  Click{' '}
                  <a
                    href="https://www.linkedin.com/in/"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-blue-50 underline hover:text-blue-60"
                  >
                    here
                  </a>{' '}
                  to find your full profile URL{' '}
                </div>
              }
            />
          </div>
        </Form>
      </div>
      {/* show different buttons based on where form is placed */}
      <div className="flex w-full justify-end">
        {placement === 'profile' ? (
          <div className="flex flex-row space-x-2">
            <Button variant="secondary" onClick={() => onCompletion()}>
              Discard
            </Button>
            <Button onClick={handleSubmit(onSubmit)}>Update Profile</Button>
          </div>
        ) : (
          <Button onClick={handleSubmit(onSubmit)}>Next</Button>
        )}
      </div>
    </div>
  );
}
